import handleYearSelected from "@bevco/Store/Handlers/SpendReporting/HandleYearSelected";
import loadPurchasesByProduct from "@bevco/Store/Handlers/SpendReporting/LoadPurchasesByProduct";
import loadSpendReport from "@bevco/Store/Handlers/SpendReporting/LoadSpendReport";
import { CustomState } from "@bevco/Store/Reducers";
import useAmountLabel from "@bevco/Utilities/Hooks/useAmountLabelChart";
import useMonthLabels from "@bevco/Utilities/Hooks/useChartLabelsMonths";
import { colorStrings, mediaQueriesBP } from "@bevco/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { Chart } from "chart.js";
import debounce from "lodash/debounce";
import React, { createRef, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    labels = "labels",
    incrementYAxis = "incrementYAxis",
}

const enum labels {
    month1 = "Jan",
    month2 = "Feb",
    month3 = "Mar",
    month4 = "Apr",
    month5 = "May",
    month6 = "Jun",
    month7 = "Jul",
    month8 = "Aug",
    month9 = "Sep",
    month10 = "Oct",
    month11 = "Nov",
    month12 = "Dec",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.labels]: LabelModel[];
        [fields.incrementYAxis]: number;
    };
}

interface LabelModel {
    fields: {
        id: number;
        name: string;
    };
}

const mapStateToProps = (state: CustomState) => {
    const purchasesByProductState = state.customPurchasesByProductState;
    const yearSelected = purchasesByProductState.yearSelected ?? new Date().getFullYear();
    const productGroupsData = purchasesByProductState?.data.groups ?? [];
    const years = purchasesByProductState?.data.availableYears
        ? [...purchasesByProductState.data.availableYears].sort((a, b) => b - a)
        : [];

    return {
        isAuthenticated: getSession(state).isAuthenticated,
        isLoading: purchasesByProductState.isLoading,
        yearSelected,
        years,
        productGroupsData,
    };
};

const mapDispatchToProps = { handleYearSelected, loadSpendReport, loadPurchasesByProduct };

export interface PurchasesByProductStyles {
    wrapper?: InjectableCss;
    titleLabel?: TypographyPresentationProps;
    header?: InjectableCss;
    summaryLabel?: TypographyPresentationProps;
    summaryValue?: TypographyPresentationProps;
    yearSelectorWrapper?: InjectableCss;
    yearSelector?: SelectPresentationProps;
    loadingWrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
}

export const purchasesByProductStyles: PurchasesByProductStyles = {
    wrapper: {
        css: css`
            border: 1px solid ${getColor(colorStrings.commonGlobalGray02)};
            display: flex;
            flex-direction: column;
            gap: 24px;
            padding: 40px;
            position: relative;

            ${mediaQueriesBP.maxMD} {
                gap: 16px;
                padding: 16px;
            }
        `,
    },
    titleLabel: {
        variant: "h2",
        transform: "uppercase",
        css: css`
            margin: 0;
        `,
    },
    header: {
        css: css`
            display: flex;
            gap: 24px;

            ${mediaQueriesBP.maxMD} {
                flex-direction: column;
                gap: 6px;
            }

            ${mediaQueriesBP.minMD} {
                align-items: center;
                justify-content: space-between;
            }
        `,
    },
    summaryValue: {
        weight: 700,
    },
    yearSelectorWrapper: {
        css: css`
            display: inline-flex;
            justify-content: flex-end;
            margin-inline-start: auto;
        `,
    },
    loadingWrapper: {
        css: css`
            align-items: center;
            background-color: ${safeColor(getColor(colorStrings.commonGlobalWhite)).rgb().alpha(0.06).string()};
            display: inline-flex;
            height: 100%;
            justify-content: center;
            position: absolute;
            right: 0;
            top: 0;
            width: 100%;
        `,
    },
};

const styles = purchasesByProductStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

const PurchasesByProduct: React.FC<Props> = ({
    shellContext: { isEditing },
    fields: { title, labels: months, incrementYAxis },
    isAuthenticated,
    isLoading,
    yearSelected,
    years,
    productGroupsData,
    handleYearSelected,
    loadPurchasesByProduct,
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const canvasRef = createRef<HTMLCanvasElement>();
    const [chartData, setChartData] = useState<Chart>();
    const { callbackAmountLabel } = useAmountLabel();
    const { callbackMonthLabels } = useMonthLabels(isMobile, months);

    const calculateAspectRatio = isMobile ? 3 / 4 : 21 / 9;

    const colors = ["#0077B6", "#009660", "#F8B241", "#7263CA", "#E82011"];

    if (isEditing || !isAuthenticated) {
        if (!isAuthenticated) {
            return null;
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.titleLabel}>{title}</Typography>
                <Typography>Edit Mode</Typography>
            </StyledWrapper>
        );
    }

    const handleChartResize = () => {
        setIsMobile(window.matchMedia("(max-width: 425px)").matches);
    };

    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        handleYearSelected({ yearSelected: +event.target.value, typeChart: "purchases-by-product" });
    };

    const buildDatasets = () => {
        // Get unique product groups across all months while preserving order
        const productGroups = Array.from(
            new Set(
                productGroupsData.reduce(
                    (acc, month) => [...acc, ...month.categories.map(category => category.productGroupName)],
                    [] as string[],
                ),
            ),
        );

        // Create datasets preserving order of appearance
        return productGroups.map((productGroup, index) => {
            const monthlyData = Array(12).fill(0);

            productGroupsData.forEach(monthData => {
                const category = monthData.categories.find(c => c.productGroupName === productGroup);
                if (category) {
                    monthlyData[Number(monthData.month) - 1] = category.totalAmount;
                }
            });

            return {
                label: productGroup,
                data: monthlyData,
                backgroundColor: colors[index % colors.length],
                borderWidth: 1,
                order: index,
            };
        });
    };

    // we transform translate the tooltip to the right position only if it doesn touch the right border
    // of the canvas
    const calculateTooltipPosition = (tooltipEl: any, chart: any) => {
        const positionX = Number(chart.canvas.offsetLeft);
        const tooltipWidth = Number(tooltipEl.offsetWidth);
        const tooltipX = positionX + Number(chart.tooltip.caretX);

        if (tooltipX + tooltipWidth > Number(chart.canvas.offsetWidth)) {
            tooltipEl.style.transform = "translate(-80%, 0)";
        } else {
            tooltipEl.style.transform = "translate(20%, 0)";
        }
    };

    const getOrCreateTooltip = (chart: any) => {
        let tooltipEl = chart.canvas.parentNode.querySelector("div");

        if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            calculateTooltipPosition(tooltipEl, chart);

            tooltipEl.style.background = "rgba(0, 0, 0, 0.7)";
            tooltipEl.style.borderRadius = "3px";
            tooltipEl.style.color = "white";
            tooltipEl.style.opacity = 1;
            tooltipEl.style.pointerEvents = "none";
            tooltipEl.style.position = "absolute";
            tooltipEl.style.transition = "all .1s ease";
            tooltipEl.style.fontFamily = `'myriad-pro-condensed',sans-serif;`;
            tooltipEl.style.fontSize = "12px";

            const table = document.createElement("table");
            table.style.margin = "0px";

            tooltipEl.appendChild(table);
            chart.canvas.parentNode.appendChild(tooltipEl);
        }

        return tooltipEl;
    };

    const externalTooltipHandler = (context: any) => {
        const { chart, tooltip } = context;
        const tooltipEl = getOrCreateTooltip(chart);
        calculateTooltipPosition(tooltipEl, chart);

        // Hide if no tooltip
        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
        }

        // Set Text
        if (tooltip.body) {
            const bodyLines = tooltip.body.map((b: any) => b.lines);

            const tableHead = document.createElement("thead");

            const tableBody = document.createElement("tbody");
            bodyLines.forEach((body: string[], i: number) => {
                const colors = tooltip.labelColors[i];

                const span = document.createElement("span");
                span.style.background = colors.backgroundColor;
                span.style.borderColor = colors.borderColor;
                span.style.borderWidth = "2px";
                span.style.marginRight = "10px";
                span.style.height = "10px";
                span.style.width = "10px";
                span.style.display = "inline-block";

                const tr1 = document.createElement("tr");
                tr1.style.backgroundColor = "transparent";
                tr1.style.borderWidth = "0";

                const tr2 = document.createElement("tr");
                tr2.style.backgroundColor = "transparent";
                tr2.style.borderWidth = "0";

                const td = document.createElement("td");
                td.style.borderWidth = "0";
                td.style.height = "auto";

                const td2 = document.createElement("td");
                td2.style.borderWidth = "0";
                td2.style.height = "auto";

                const productGroupTitle = document.createTextNode(`${body[0].split(":")[0]}`);
                const productGroupValue = document.createTextNode(
                    `${translate("Purchases ($)")} ${body[0].split(":")[1]}`,
                );

                // Append elements to the first row
                td.appendChild(span);
                td.appendChild(productGroupTitle);
                tr1.appendChild(td);

                // Append elements to the second row
                td2.appendChild(productGroupValue);
                tr2.appendChild(td2);

                // Append rows to the table body
                tableBody.appendChild(tr1);
                tableBody.appendChild(tr2);
            });

            const tableRoot = tooltipEl.querySelector("table");

            // Remove old children
            while (tableRoot.firstChild) {
                tableRoot.firstChild.remove();
            }

            // Add new children
            tableRoot.appendChild(tableHead);
            tableRoot.appendChild(tableBody);
        }

        const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

        // Display, position, and set styles for font
        tooltipEl.style.opacity = 1;
        tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
        tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
        tooltipEl.style.font = tooltip.options.bodyFont.string;
        tooltipEl.style.padding = `${tooltip.options.padding}px ${tooltip.options.padding}px`;
    };

    /* eslint-disable */
    const Sorted = {
        id: "SortedStack",
        beforeDraw: function (chart: any, args: any, options: any) {
            options.sortedData = [];

            for (var datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
                var dataset = chart.data.datasets[datasetIndex];

                for (var index = 0; index < dataset.data.length; index++) {
                    if (!options.sortedData[index]) {
                        options.sortedData[index] = {
                            data: [],
                        };
                    }

                    options.sortedData[index].data[datasetIndex] = {
                        datasetIndex: datasetIndex,
                        hidden: chart.getDatasetMeta(datasetIndex).hidden ? true : false,
                        value: dataset.data[index],
                        y: chart.getDatasetMeta(datasetIndex).data[index].y,
                        base: chart.getDatasetMeta(datasetIndex).data[index].base,
                    };
                }
            }

            const chartTop = chart.scales.y.top;
            const max = chart.scales.y.max;
            const h = chart.scales.y.height / max;

            for (var datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
                for (var index = 0; index < chart.data.datasets[datasetIndex].data.length; index++) {
                    options.sortedData[index].data.sort((a: any, b: any) => a.value - b.value);

                    for (var i = 0; i < options.sortedData[index].data.length; i++) {
                        var d = options.sortedData[index].data[i];

                        d.base =
                            chartTop +
                            (max - options.sortedData[index].data.reduce((a: number, b: any) => a + b.value, 0)) * h +
                            options.sortedData[index].data
                                .filter((d: any) => d.hidden)
                                .reduce((a: number, b: any) => a + b.value, 0) *
                                h;

                        for (var j = 0; j < i; j++) {
                            d.base += options.sortedData[index].data[j].hidden
                                ? 0
                                : h * options.sortedData[index].data[j].value;
                        }

                        d.y = d.base + h * d.value;
                    }
                }
            }
        },
        beforeDatasetDraw: function (chart: any, args: any, options: any) {
            chart.getDatasetMeta(args.index).data.forEach((data: any, index: number) => {
                var el = options.sortedData[index].data.filter((e: any) => e.datasetIndex === args.index)[0];
                data.y = el.y;
                data.base = el.base;
            });
        },
    };
    /* eslint-enable */

    const buildChartData = () => {
        const labels: string[] = [];
        for (let i = 1; i <= 12; i++) {
            const label = months.find(item => item.fields.id === i);
            if (label) {
                labels.push(label.fields.name);
            } else {
                labels.push(`${i}`);
            }
        }

        const chartBox = document.querySelector(".purchases-report__chart");

        if (!chartBox) {
            return;
        }

        if (chartData) {
            // eslint-disable-next-line no-console
            console.info("Destroying chart");
            chartData.destroy();
            setChartData(undefined);
        }

        const newChartData: Chart = new Chart(chartBox as any, {
            type: "bar",
            data: {
                labels,
                datasets: buildDatasets(),
            },
            options: {
                aspectRatio: calculateAspectRatio,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: false,
                        position: "nearest",
                        external: externalTooltipHandler,
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        ticks: {
                            callback: callbackMonthLabels,
                        },
                    },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        position: "left",
                        min: 0,
                        ticks: {
                            stepSize: incrementYAxis,
                            callback: callbackAmountLabel,
                        },
                    },
                },
            },
            plugins: [Sorted],
        });

        // eslint-disable-next-line no-console
        console.info("Chart created");
        setChartData(newChartData);
    };

    useEffect(() => {
        loadPurchasesByProduct({});

        setIsMobile(window.matchMedia("(max-width: 425px)").matches);
        window.addEventListener("resize", debounce(handleChartResize, 500));

        return () => {
            window.removeEventListener("resize", handleChartResize);
        };
    }, []);

    useEffect(() => {
        if (productGroupsData != null) {
            buildChartData();
        }
    }, [productGroupsData]);

    React.useEffect(() => {
        if (chartData) {
            chartData.options.aspectRatio = calculateAspectRatio;
            chartData.resize();
            chartData.update();
        }
    }, [isMobile]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.titleLabel}>{title}</Typography>

            <StyledWrapper {...styles.header}>
                <StyledWrapper {...styles.yearSelectorWrapper}>
                    <StyledWrapper>
                        {years && (
                            <Select {...styles.yearSelector} value={yearSelected} onChange={handleYearChange}>
                                {years.length === 0 && <option value={yearSelected}>{yearSelected}</option>}
                                {years.length > 0 &&
                                    [...years].map(value => (
                                        <option key={value} value={value}>
                                            {value}
                                        </option>
                                    ))}
                            </Select>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <div className="chart-view">
                <canvas className="purchases-report__chart" ref={canvasRef}></canvas>
            </div>

            {isLoading && (
                <StyledWrapper {...styles.loadingWrapper}>
                    <LoadingSpinner {...styles.loadingSpinner} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(PurchasesByProduct) as any) as any,
    definition: {
        group: "My Account",
        displayName: "Purchases By Product Chart",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Spending Report",
                isRequired: true,
            },
            {
                name: fields.labels,
                displayName: "Months Labels",
                editorTemplate: "ListField",
                defaultValue: [
                    { fields: { id: 1, name: labels.month1 } },
                    { fields: { id: 2, name: labels.month2 } },
                    { fields: { id: 3, name: labels.month3 } },
                    { fields: { id: 4, name: labels.month4 } },
                    { fields: { id: 5, name: labels.month5 } },
                    { fields: { id: 6, name: labels.month6 } },
                    { fields: { id: 7, name: labels.month7 } },
                    { fields: { id: 8, name: labels.month8 } },
                    { fields: { id: 9, name: labels.month9 } },
                    { fields: { id: 10, name: labels.month10 } },
                    { fields: { id: 11, name: labels.month11 } },
                    { fields: { id: 12, name: labels.month12 } },
                ],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: true,
                hideEdit: false,
                hideDelete: true,
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.incrementYAxis,
                displayName: "Increment Y Axis",
                editorTemplate: "IntegerField",
                defaultValue: 1000,
                min: 1000,
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
